import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { LineOfBusiness} from '@core/enumeration';
import { MemberInfoPacim, PrimaryCarePhysician } from '@core/model';
import { AuthenticationService, MemberPacService } from '@core/service';
import { EmployerIdentifier, FundingTypes } from '@shared/enumeration';
import { MemberBenefitDays, MemberShortData } from '@shared/model';
import { calculateAge } from '@shared/utility';
import { DualsPlanType, StateShortName, TenantNames } from '@mynexus/mynexus-ui-lib';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'member-detail-ribbon',
    templateUrl: './member-detail-ribbon.component.html',
    styleUrls: ['./member-detail-ribbon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MemberDetailRibbonComponent implements OnChanges {
  @Input() showExtendData: boolean;
  @Input() showLoader: boolean;
  @Input() memberData: MemberShortData;
  @Input() memberInfo?: MemberInfoPacim;
  @Input() snfBenefitDaysRemaining: MemberBenefitDays;
  @Input() facilityType: string;
  @Input() tenantId: string;
  @Input() primaryCarePhysician: PrimaryCarePhysician;
  @Input() utilizationReviewNumber: string;

  protected readonly DualsPlanType = DualsPlanType;

  groupId : string;
  readonly groupIdApplicableTenants : TenantNames[] = [TenantNames.Anthem, TenantNames.Aetna, TenantNames.Regence];
  gender!: string;
  age!: string;
  specialtyProgram!: string;
  c3!: string;
  assignedLiaison!: string;
  snfBenefitDays: number = undefined;
  snfExhaustedDate: string;
  snfBenefitExhausted: string;
  pTooltipMessage = "Download Explanation Of Coverage";
  private maStateMandateBenefitPlans: string[] = [];
  eocDeeplink = '';
  lineOfBusiness: { text: string; class: string; } | null;
  showCovaIdentifier = false;
  showOHIOIdentifier = false;
  isOHIO = false;
  isErisaIndicator= false;
  employeWaGroup = ['W12667', 'W12652', 'W12653', 'W24174']
  showWaGroupIdentifier = false;
  isExternal: boolean;

  private readonly genderMap = {
    M: 'Male',
    Male: 'Male',
    F: 'Female',
    Female: 'Female',
  };

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly memberPacService: MemberPacService,
    authentication: AuthenticationService,
  ) {
    this.isExternal = authentication.isExternalUser;
  }

  ngOnInit(): void {
    this.loadMaStateMandateConfiguration();
  }

  private loadMaStateMandateConfiguration(): void {
    this.memberPacService.getMaStateMandateBenefitPlans(TenantNames.Anthem)
      .subscribe({
        next: (plans) => {
          this.maStateMandateBenefitPlans = plans || [];
          this.ref.detectChanges();
        },
        error: () => {
          this.maStateMandateBenefitPlans = [];
          this.ref.detectChanges();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { memberData, snfBenefitDaysRemaining } = changes;

    if (memberData && memberData.currentValue) {
      this.gender = this.genderMap[this.memberData.member.gender];
      this.getLineOfBusinessMark();
      this.age = `${calculateAge(this.memberData.member.dateOfBirth)} year(s) old`;
      
      const processedPrograms = this.manageSdohSpecialtyProgram(
        this.memberData.member.specialtyProgram,
        this.memberData.benefit.healthPlan,
        this.memberData?.benefit.isEligible,
        this.memberData?.benefit.lineOfBusiness
      );
      this.specialtyProgram = processedPrograms?.join(', ');
      
      this.c3 = this.memberData.member.c3 ? 'Yes' : 'No';
      this.assignedLiaison = this.memberData.member.assignedLiaison ? 'Yes' : 'No';
      if(this.lineOfBusiness?.text === LineOfBusiness.Commercial) {
        this.getERISAReferenceDetails(this.memberData.member.memberId);
      }

      // PAC MA should always be SNF while everything else (Commercial right now) is dynamic
      this.facilityType = this.memberData.benefit.lineOfBusiness === LineOfBusiness.MedicareAdvantage ? 'SNF' : (this.facilityType ?? '');
      this.isOHIO = this.memberData.benefit.benefitPlan === 'OH_QB0B_W59989M001_PPO (STATE OF OHIO)';
    }

    if (snfBenefitDaysRemaining && snfBenefitDaysRemaining.currentValue) {
      const { daysRemaining, exhaustedDate } = this.snfBenefitDaysRemaining;

      this.snfBenefitDays = daysRemaining ? Math.max(0, daysRemaining) : daysRemaining;
      this.snfExhaustedDate = exhaustedDate;
    }
  }

  private getERISAReferenceDetails(memberId: string) {
    this.memberPacService.getERISAReferenceDetails(this.tenantId, memberId).subscribe(res => {
      if(res) {
        const employerIdentifier = res.employerIdentifier?.trim();
        this.showCovaIdentifier = employerIdentifier === EmployerIdentifier.Cova;
        this.showOHIOIdentifier = employerIdentifier === EmployerIdentifier.OHIO;
        this.showWaGroupIdentifier = this.employeWaGroup.includes(employerIdentifier);
        this.isErisaIndicator = res.isERISA;
        this.ref.detectChanges();
      }
    });
  }


  private getLineOfBusinessMark(): void {
    const { lineOfBusinessApplicationKey, lineOfBusiness, mcoState, mcoName } = this.memberData.benefit;

    if (!lineOfBusiness) {
      this.lineOfBusiness = null;
    }

    switch (lineOfBusinessApplicationKey || lineOfBusiness) {
      case LineOfBusiness.MedicareAdvantage:
        this.lineOfBusiness = { text: 'MA', class: 'ma' };
        break;

      case LineOfBusiness.Commercial:
        this.lineOfBusiness = { text: lineOfBusiness, class: 'commercial' };
        break;

      default:
        this.lineOfBusiness = {
          text: mcoState && mcoName ? `Medicaid ${mcoState} / ${mcoName}` : lineOfBusiness,
          class: 'active'
        }
        break;
    }
  }

  get  getTooltipMessage() : string {
  if(this.memberData !== null && this.memberData.benefit!= null && this.eocDeeplink === '' ){
    this.memberPacService.getEOCExplanation(this.tenantId , this.memberData?.benefit?.benefitPlan)
      .subscribe( (res) => {
        if (res === null) {
          this.eocDeeplink = res;
          this.pTooltipMessage = "Document unavailable, please refer to the EOC process/job aid" ;
          this.ref.detectChanges();
        }else {
          this.eocDeeplink = res
        }
      })
  }
  return  this.pTooltipMessage
}

downloadDocument() {
  if (this.eocDeeplink !==  null) {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this.eocDeeplink);
    link.setAttribute('download','XYZ');
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
shouldShowGroupId(): boolean {
  this.groupId = this.memberData?.benefit?.groupId;
  return this.groupIdApplicableTenants.includes(this.memberData?.benefit.healthPlan as TenantNames) && this.lineOfBusiness?.text === 'MA';
}



  showFundingIndicator(): boolean {
    const healthPlanState = this.memberData?.benefit.programState;
    const eligibleStates: string[] = [
      StateShortName.Maryland,
      StateShortName.Colorado,
      StateShortName.NewHampshire,
      StateShortName.Georgia,
      StateShortName.Kentucky,
      StateShortName.Maine,
      StateShortName.Indiana,
      StateShortName.Nevada
    ];

    const fundingType = this.getFundingType();
    const isEligibleFunding = fundingType === FundingTypes.FI || fundingType === FundingTypes.ASO;

    return eligibleStates.includes(healthPlanState as string) && isEligibleFunding;
  }

  getFundingType(): string | undefined {
    return this.memberData?.benefit?.fundingArrangement;
  }

  getFundingTypeTooltipText(): string {
    const fundingType = this.getFundingType();

    switch (fundingType) {
      case FundingTypes.FI:
        return 'Fully Insured';
      case FundingTypes.ASO:
        return this.isErisaIndicator ? 'Erisa ASO' : 'Non-Erisa ASO';
      default:
        return '';
    }
  }

  private manageSdohSpecialtyProgram(
    specialtyPrograms: string[] | null | undefined,
    healthPlanName: string | null | undefined,
    isEligible: boolean | null | undefined,
    lob: string | null | undefined
  ): string[] {
    let programs = specialtyPrograms ? [...specialtyPrograms] : [];
    
    const isAnthemTenant = healthPlanName?.toLowerCase() === TenantNames.Anthem.toLowerCase();
    const isActiveMember = isEligible === true;
    const isMedicareAdvantageLOB = lob?.toLowerCase() === LineOfBusiness.MedicareAdvantage.toLowerCase();
    const qualifiesForSdoh = isAnthemTenant && isActiveMember && isMedicareAdvantageLOB;
    
    if (qualifiesForSdoh) {
      if (!programs.some(p => p?.toLowerCase() === 'sdoh')) {
        programs.push('SDoH');
      }
    } else {
      programs = programs.filter(p => p?.toLowerCase() !== 'sdoh');
    }
    
    return programs;
  }

  showInMandateApplies(): boolean {
    const healthPlanState = this.memberData?.benefit.programState;
    const isCommercialLOB = this.lineOfBusiness?.text === LineOfBusiness.Commercial
    return (
      healthPlanState === StateShortName.Indiana &&
      isCommercialLOB
    );
  }

  showMaStateMandateMessage(): boolean {
    const benefitPlan = this.memberData?.benefit?.benefitPlan;
    return (
      Array.isArray(this.maStateMandateBenefitPlans) &&
      this.maStateMandateBenefitPlans.includes(benefitPlan) &&
      this.lineOfBusiness?.text === 'MA' &&
      this.memberData?.benefit?.healthPlan === TenantNames.Anthem
    );
  }
}
